import type { Component, Focusable } from "@earendil-works/pi-tui";
import type { AgentSessionEvent, CompactionReason } from "../../../core/agent-session.js";
import { repairOrphanToolResults } from "../../../core/messages.ts";
import { SessionManager } from "../../../core/session-manager.ts";
import type { SessionStore } from "../../../core/storage/session/types.js";
import type { TaskId } from "../../../core/tasks/contracts.js";
import { getOwnerTaskStore, type OwnerTaskStore, watchOwnerTaskStoreBinding } from "../../../core/tasks/owner-store.js";
import {
	abortChatSessionBash,
	abortChatSessionCompaction,
	interruptChatSession,
	restoreQueuedMessagesToEditor,
	submitChatSession,
} from "./chat-session-host-actions.ts";
import {
	createChatSessionEditor,
	handleChatSessionInput,
	setChatSessionEditorText,
} from "./chat-session-host-editor.ts";
import { applyChatSessionAgentEvent, compactionStatusMessage } from "./chat-session-host-events.ts";
import {
	renderChatSessionBody,
	renderChatSessionEditor,
	renderChatSessionEntry,
	renderChatSessionFooter,
	renderChatSessionPendingMessages,
	renderChatSessionUsage,
	renderChatSessionWorkingStatus,
	transcriptCacheKey,
} from "./chat-session-host-rendering.ts";
import {
	clearChatSessionBusyForTerminalWorkflowStage,
	disposeChatSession,
	isChatSessionBashRunning,
	isChatSessionStreaming,
	reassertChatSessionExternalPromptLifecycle,
	settleChatSessionPromptLifecycle,
	startChatSessionExternalPromptLifecycle,
	syncChatSessionAnimationTick,
} from "./chat-session-host-runtime.ts";
import { ChatSessionHostState } from "./chat-session-host-state.ts";
import type {
	AgentSnapshotMessage,
	ChatSessionHostEntry,
	ChatSessionHostOpts,
	ChatSessionSubmitMode,
} from "./chat-session-host-types.ts";
import type { ChatTranscriptEntryLike } from "./chat-transcript.js";
import { TaskInspector } from "./task-inspector.js";
import { renderTaskFooter } from "./task-list.js";

export type {
	ChatSessionHostBashRequest,
	ChatSessionHostCommands,
	ChatSessionHostEntry,
	ChatSessionHostOpts,
	ChatSessionHostStyle,
	ChatSessionSubmitMode,
} from "./chat-session-host-types.ts";

export class ChatSessionHost<TExtraEntry extends ChatTranscriptEntryLike = never> implements Component, Focusable {
	focused = true;

	private readonly state: ChatSessionHostState<TExtraEntry>;
	private taskStore?: OwnerTaskStore;
	private readonly taskRowsInChat: boolean;
	private unsubscribeTasks?: () => void;
	private taskSession?: object;
	private unsubscribeTaskBinding?: () => void;
	private taskInspector?: TaskInspector;
	private historyHydrated = false;

	constructor(opts: ChatSessionHostOpts<TExtraEntry>) {
		// `/tasks` stays a local action owned by the host's `commands.handleSlashCommand`
		// (see submitChatSession). Hosts that mount the inspector call `openTasks` from
		// that callback; the host must not intercept it ahead of the owner.
		this.taskRowsInChat = opts.taskRowsInChat ?? true;
		this.state = new ChatSessionHostState(opts, {
			renderEntry: (state, entry) => renderChatSessionEntry(state, entry),
			transcriptCacheKey: (state, entry, index) => transcriptCacheKey(state, entry, index),
		});
		this.state.editor = createChatSessionEditor(
			this.state,
			opts.tui,
			opts.keybindings,
			opts.editorTheme,
			opts.editorFactory,
			this.editorCallbacks(),
		);
		if (opts.autocompleteProvider) this.state.editor?.setAutocompleteProvider?.(opts.autocompleteProvider);
		this.syncAnimationTick();
		this.refreshTaskStore();
	}

	openTasks(id?: string): boolean {
		const session = this.state.getAgentSession?.();
		if (session && !getOwnerTaskStore(session)) session.getAgentTaskHost?.();
		this.refreshTaskStore();
		if (!this.taskStore) {
			this.showWarning("Launched agents and shells will appear here.");
			return true;
		}
		this.taskInspector?.dispose();
		this.taskInspector = new TaskInspector(
			this.taskStore,
			() => this.state.requestRender?.(),
			() => this.closeTasks(),
		);
		this.taskInspector.open(id as TaskId | undefined);
		this.state.requestRender?.();
		return true;
	}
	get hasTaskInspector(): boolean {
		return this.taskInspector !== undefined;
	}
	get taskInspectorFullscreen(): boolean {
		return this.taskInspector?.fullscreen === true;
	}
	closeTasks(): void {
		this.taskInspector?.dispose();
		this.taskInspector = undefined;
		this.state.requestRender?.();
	}
	handleTaskInput(data: string): boolean {
		if (!this.taskInspector) return false;
		this.taskInspector.handleInput(data);
		return true;
	}
	appendMessages(messages: readonly AgentSnapshotMessage[]): void {
		this.state.liveChat.appendMessages(messages);
	}

	/**
	 * Adopt the assistant message a live session is part-way through streaming.
	 *
	 * A host mounted mid-turn missed the `message_start` and every delta that
	 * came before it, and delta-only updates never restate them, so the session's
	 * in-flight message is the only place that text still exists. Ignored once
	 * this host is already following a stream of its own.
	 */
	hydrateStreamingAssistantMessage(message: AgentSnapshotMessage | undefined): void {
		if (message === undefined) return;
		if (this.state.liveChat.hydrateStreamingAssistantMessage(message)) this.state.requestRender?.();
	}

	async loadSession(store: SessionStore, sessionId: string | undefined): Promise<boolean> {
		if (
			this.historyHydrated ||
			this.state.transcript.length > this.state.extraEntries.length ||
			sessionId === undefined
		)
			return true;
		let messages: readonly AgentSnapshotMessage[];
		try {
			const manager = await SessionManager.open(store, sessionId);
			messages = repairOrphanToolResults(manager.buildSessionContext().messages, {
				repairTrailing: true,
			}) as readonly AgentSnapshotMessage[];
		} catch {
			return false;
		}
		if (this.state.disposed) return false;
		if (this.historyHydrated || this.state.transcript.length > this.state.extraEntries.length) return true;
		this.historyHydrated = true;
		const extras = this.state.transcript.splice(0);
		this.state.liveChat.appendMessages(messages);
		this.state.transcript.push(...extras);
		this.state.requestRender?.();
		return true;
	}

	appendExtraEntry(entry: TExtraEntry): void {
		this.state.extraEntries.push(entry);
		this.state.transcript.push(entry);
	}

	entries(): readonly ChatSessionHostEntry<TExtraEntry>[] {
		return this.state.transcript;
	}

	/** Tasks outlive tools and turns; only host/session binding grants this read projection. */
	refreshTaskStore(): void {
		const session = this.state.getAgentSession?.();
		const sessionChanged = session !== this.taskSession;
		if (sessionChanged) {
			this.unsubscribeTaskBinding?.();
			this.taskSession = session;
			this.unsubscribeTaskBinding = session
				? watchOwnerTaskStoreBinding(session, () => this.refreshTaskStore())
				: undefined;
		}
		const store = session ? getOwnerTaskStore(session) : undefined;
		if (store === this.taskStore) return;
		this.closeTasks();
		this.unsubscribeTasks?.();
		this.taskStore = store;
		this.unsubscribeTasks = undefined;
		this.state.liveChat.clearTasks();
		this.state.transcriptComponent.invalidate();
		this.state.requestRender?.();
		if (!store) return;
		const update = () => {
			if (this.taskRowsInChat) this.state.liveChat.upsertTasks(store.tasks, store);
			this.state.transcriptComponent.invalidate();
			this.state.requestRender?.();
		};
		this.unsubscribeTasks = store.subscribe(update);
		update();
	}
	applyAgentEvent(event: AgentSessionEvent): boolean {
		this.refreshTaskStore();
		return applyChatSessionAgentEvent(this.state, event);
	}

	/**
	 * Restore the factual compaction indicator on a host mounted after the
	 * `compaction_start` it never saw. The label resolves through the same
	 * mapping the live event path uses, so the two cannot drift.
	 *
	 * Branch summaries deliberately do not paint here: their label belongs to
	 * a retry lifecycle this re-attached host is not inside, and no event exists
	 * to clear it after the summary finishes. An absent paintable reason is a
	 * no-op unless this host is actively showing a compaction label, so unrelated
	 * workflow delivery busy state is preserved.
	 */
	hydrateCompactionStatus(reason: CompactionReason | undefined): void {
		const compactionReason = reason === "branchSummary" ? undefined : reason;
		if (compactionReason === undefined) {
			if (!this.state.compacting) return;
			this.state.compacting = false;
			this.state.statusMessage = "";
		} else {
			this.state.compacting = true;
			this.state.sdkBusy = true;
			this.state.statusMessage = compactionStatusMessage(compactionReason);
		}
		this.syncAnimationTick();
		this.state.requestRender?.();
	}

	render(width: number): string[] {
		return this.renderBody(width, 1);
	}

	invalidate(): void {
		this.taskInspector?.invalidate();
		this.state.transcriptComponent.invalidate();
		this.state.bodyViewport.invalidate();
		this.state.editor?.invalidate();
	}

	renderBody(width: number, budget: number): string[] {
		if (this.taskInspector) {
			if (this.taskInspector.fullscreen) return this.taskInspector.renderViewport(width, budget);
			const picker = this.taskInspector.renderPicker(width, budget);
			const remaining = Math.max(0, budget - picker.length);
			const body = remaining ? renderChatSessionBody(this.state, width, remaining) : [];
			return [...body, ...Array.from({ length: Math.max(0, remaining - body.length) }, () => ""), ...picker];
		}
		return renderChatSessionBody(this.state, width, budget);
	}

	/**
	 * Rows the body occupies at `width`, whether or not they are on screen.
	 *
	 * Pair with `renderBodyRows` to read the whole transcript, not only the
	 * window the reader is parked on. Both measure the component stack the last
	 * `renderBody` installed, so call them after at least one body render.
	 */
	bodyRowCount(width: number): number {
		return this.state.bodyViewport.rowCount(width);
	}

	/** Body rows `startRow` (inclusive) to `endRow` (exclusive), unscrolled. */
	renderBodyRows(width: number, startRow: number, endRow: number): string[] {
		return this.state.bodyViewport.renderRows(width, startRow, endRow);
	}

	/**
	 * Render a live assistant entry whole, or only its tail (the default).
	 *
	 * The tail window keeps a fast-streaming turn cheap for a reader following
	 * the bottom of the body; it also drops everything above the last 240 lines
	 * from what `bodyRowCount` and `renderBodyRows` report. Returns whether the
	 * setting changed, so the caller can invalidate the rows that were rendered
	 * under the old rule.
	 */
	setStreamingTailWindowEnabled(enabled: boolean): boolean {
		if (this.state.streamingTailWindowEnabled === enabled) return false;
		this.state.streamingTailWindowEnabled = enabled;
		this.invalidate();
		return true;
	}

	renderPendingMessages(width: number): string[] {
		return renderChatSessionPendingMessages(this.state, width);
	}

	renderWorkingStatus(width: number): string[] {
		return renderChatSessionWorkingStatus(this.state, width);
	}

	renderUsage(width: number): string[] {
		return renderChatSessionUsage(this.state, width);
	}

	renderEditor(width: number): string[] {
		return renderChatSessionEditor(this.state, width, this.focused);
	}

	renderTaskFooter(width: number): string[] {
		return renderTaskFooter(this.taskStore?.backgroundTasks ?? [], width);
	}
	renderFooter(width: number): string[] {
		const footer = renderChatSessionFooter(this.state, width, this.hasTaskInspector && !this.taskInspectorFullscreen);
		return footer.length ? footer : renderTaskFooter(this.taskStore?.backgroundTasks ?? [], width);
	}

	handleScrollInput(data: string): boolean {
		return this.state.bodyViewport.handleInput(data);
	}

	handleInput(data: string): boolean {
		return handleChatSessionInput(this.state, data, this.editorCallbacks());
	}

	interrupt(options?: { restoreQueuedMessages?: boolean }): Promise<void> {
		// Keep the observed settlement: an async wrapper would orphan rejections from Escape callers.
		return interruptChatSession(this.state, options);
	}

	async submit(mode: ChatSessionSubmitMode = "auto", submittedText?: string): Promise<void> {
		await submitChatSession(this.state, mode, submittedText);
	}

	isStreaming(): boolean {
		return isChatSessionStreaming(this.state);
	}

	isBashRunning(): boolean {
		return isChatSessionBashRunning(this.state);
	}

	isEditingBashCommand(): boolean {
		return this.state.isBashMode;
	}

	isCompacting(): boolean {
		return this.state.compacting;
	}

	hasInputText(): boolean {
		return this.state.inputBuffer.length > 0;
	}

	hasAnimationTick(): boolean {
		return this.state.animationTimer !== undefined;
	}

	bodyScrollFromBottom(): number {
		return this.state.bodyViewport.getScrollFromBottom();
	}

	bodyMaxScroll(): number {
		return this.state.bodyViewport.getMaxScroll();
	}

	inputText(): string {
		return this.state.inputBuffer;
	}
	setInputText(text: string): void {
		setChatSessionEditorText(this.state, text);
	}

	statusText(): string {
		return this.state.statusMessage;
	}

	showWarning(message: string): void {
		this.state.statusMessage = message;
		this.state.lastWarningMessage = message;
		this.state.requestRender?.();
	}

	scrollToBottom(): void {
		this.state.bodyViewport.scrollToBottom();
	}

	/**
	 * Park the body with absolute row `row` at the top of its window, clamped
	 * to the last rendered layout. Used to reveal a search match the reader
	 * cannot currently see.
	 */
	scrollBodyTo(row: number): void {
		this.state.bodyViewport.scrollTo(row);
	}

	syncAnimationTick(): void {
		syncChatSessionAnimationTick(this.state);
	}

	clearBusyForTerminalWorkflowStage(): void {
		clearChatSessionBusyForTerminalWorkflowStage(this.state);
	}

	/**
	 * Start Working for an accepted prompt delivered outside this host — a
	 * workflow definition auto-sending to its own retained stage. Returns the
	 * lifecycle token to hand back to `settleExternalPromptLifecycle`.
	 */
	beginExternalPromptLifecycle(): number | undefined {
		return startChatSessionExternalPromptLifecycle(this.state);
	}

	/**
	 * Reopen Working for a delivery still active after a temporary overlay — a
	 * pre-turn compaction — cleared it. Preserves factual status text.
	 */
	reassertExternalPromptLifecycle(): number | undefined {
		return reassertChatSessionExternalPromptLifecycle(this.state);
	}

	/** Settle a lifecycle from `beginExternalPromptLifecycle`. Stale tokens no-op. */
	settleExternalPromptLifecycle(generation: number | undefined): void {
		settleChatSessionPromptLifecycle(this.state, generation);
		syncChatSessionAnimationTick(this.state);
		this.state.requestRender?.();
	}

	dispose(): void {
		this.taskInspector?.dispose();
		this.unsubscribeTasks?.();
		this.unsubscribeTaskBinding?.();
		disposeChatSession(this.state);
	}

	restoreQueuedMessagesToEditor(): boolean {
		return restoreQueuedMessagesToEditor(this.state);
	}

	private editorCallbacks(): {
		submit: (mode: "auto" | "followUp", submittedText?: string) => void | Promise<void>;
		restoreQueuedMessagesToEditor: () => boolean;
		abortCompaction: () => void | Promise<void>;
		interrupt: (options?: { restoreQueuedMessages?: boolean }) => void | Promise<void>;
		abortBash: () => void | Promise<void>;
	} {
		return {
			submit: (mode, submittedText) => this.submit(mode, submittedText),
			restoreQueuedMessagesToEditor: () => this.restoreQueuedMessagesToEditor(),
			abortCompaction: () => abortChatSessionCompaction(this.state),
			interrupt: (options) => this.interrupt(options),
			abortBash: () => abortChatSessionBash(this.state),
		};
	}
}
